import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddBusinessRounded from '@mui/icons-material/AddBusinessRounded';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import { useLocation, useNavigate } from 'react-router';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { FormField } from '@/components/form/FormField';
import { useAsync } from '@/hooks/useAsync';
import { AuthLayout } from '@/layouts/AuthLayout';
import { AuthHeader } from '@/pages/auth/AuthHeader';
import { FormError } from '@/pages/auth/FormError';
import {
  completeIndependentProfile,
  completeProfile,
  getSignupOptions,
  type ProfileInput,
} from './api';
import { NO_CONSENT, phoneValid, wantsPhone } from './consent';
import { ConsentOptions } from './ConsentOptions';
import { InstitutionSignupPage } from './billing/InstitutionSignupPage';
import { clearPlanIntent, readPlanIntent } from './billing/labels';
import { clearEnemIntent, readEnemIntent } from './enemIntent';
import { useSession } from './session';
import type { Consent, EmailRule, Institution, SignupRole } from './types';

const RESEND_SECONDS = 60;

/** Same check as the API: `pattern` must match the whole part before the @. */
function followsRule(rule: EmailRule, email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at <= 0) return false;
  const local = email.slice(0, at);
  if (email.slice(at + 1) !== rule.domain.toLowerCase()) return false;
  try {
    return new RegExp(`^(?:${rule.pattern})$`).test(local);
  } catch {
    return false;
  }
}

/** E.g. "12.00000-0@maua.br (Mauá)": the addresses expected for this role. */
function formatHint(institutions: Institution[], role: SignupRole): string {
  const parts = institutions.flatMap((i) =>
    i.emailRules
      .filter((r) => r.role === role)
      .map(
        (r) => `${r.example || `…@${r.domain}`}${institutions.length > 1 ? ` (${i.name})` : ''}`,
      ),
  );
  return [...new Set(parts)].join(' ou ');
}

/**
 * Shown instead of the app until the profile is complete: full name, student or teacher,
 * institution (students: one, plus their course; teachers: every one they teach at) and the
 * institutional e-mail that proves it, confirmed by a link. Each institution has its own
 * address format, e.g. Mauá students 12.00000-0@maua.br, USJT teachers prof.nome@ulife.com.br.
 */
export function CompleteProfilePage() {
  const { signOut } = useAuth();
  const { me, user, refresh } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  // Came from the plans page (or /instituicoes/nova): register a new institution instead.
  const [newInstitution, setNewInstitution] = useState(
    () => location.pathname.startsWith('/instituicoes/nova') || readPlanIntent() !== null,
  );
  const options = useAsync(getSignupOptions);
  const institutions = useMemo(() => options.data?.institutions ?? [], [options.data]);
  const courses = options.data?.courses ?? [];

  const [name, setName] = useState(user.name ?? '');
  const [role, setRole] = useState<SignupRole>(
    user.requestedRole === 'teacher' || user.role === 'teacher' ? 'teacher' : 'student',
  );
  // Studies on their own for the ENEM (came from /enem, or picked it): no institution at all.
  const [independent, setIndependent] = useState(() => readEnemIntent());
  const [institutionIds, setInstitutionIds] = useState<string[]>(
    user.institutions.map((i) => i.id),
  );
  const [courseId, setCourseId] = useState(user.course?.id ?? '');
  const [email, setEmail] = useState(user.institutionalEmail ?? '');
  // Nothing ticked unless the person ticks it (LGPD).
  const [consent, setConsent] = useState<Consent>(() => ({
    ...NO_CONSENT,
    ...me.consent,
    phone: me.consent?.phone ?? user.phone ?? null,
  }));
  const [touched, setTouched] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Already asked for a link (e.g. came back later): start on the "check your inbox" step.
  const [sentTo, setSentTo] = useState<string | null>(
    user.institutionalEmail && !user.institutionalEmailVerified ? user.institutionalEmail : null,
  );
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);

  const chosen = institutions.filter((i) => institutionIds.includes(i.id));
  const trimmedName = name.trim().replace(/\s+/g, ' ');
  const normalizedEmail = email.trim().toLowerCase();
  const emailValid = chosen.some((i) =>
    i.emailRules.some((r) => r.role === role && followsRule(r, normalizedEmail)),
  );
  // Signed in with Google on this very address: nothing to confirm.
  const alreadyConfirmed =
    user.institutionalEmailVerified && user.institutionalEmail === normalizedEmail;
  const hint = formatHint(chosen, role);

  const nameError =
    touched && trimmedName.split(' ').length < 2
      ? 'Digite seu nome completo (nome e sobrenome).'
      : undefined;
  const institutionError =
    touched && chosen.length === 0
      ? role === 'student'
        ? 'Escolha sua instituição.'
        : 'Marque ao menos uma instituição.'
      : undefined;
  const courseError = touched && role === 'student' && !courseId ? 'Escolha seu curso.' : undefined;
  const emailError =
    touched && chosen.length > 0 && !emailValid
      ? `Use seu e-mail institucional de ${role === 'student' ? 'estudante' : 'professor(a)'}${hint ? `, como ${hint}` : ''}.`
      : undefined;

  const phoneError =
    touched && wantsPhone(consent) && !phoneValid(consent.phone)
      ? 'Informe seu telefone com DDD para receber por telefone.'
      : undefined;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Confirmed on another tab or device: pick it up when the person comes back here.
  useEffect(() => {
    if (!sentTo) return;
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const poll = setInterval(() => void refresh(), 10_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(poll);
    };
  }, [sentTo, refresh]);

  const changeRole = (next: SignupRole | null) => {
    if (!next) return;
    setRole(next);
    // Students have a single institution.
    if (next === 'student') setInstitutionIds((ids) => ids.slice(0, 1));
  };

  const toggleInstitution = (id: string) =>
    setInstitutionIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const input = (): ProfileInput => ({
    name: trimmedName,
    role,
    institutionIds,
    courseId: role === 'student' ? courseId : null,
    institutionalEmail: normalizedEmail,
    consent,
  });

  const send = async () => {
    setPending(true);
    setError(null);
    try {
      const { status } = await completeProfile(input());
      if (status === 'done') {
        await refresh();
        return;
      }
      setSentTo(normalizedEmail);
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível enviar o e-mail. Tente de novo.',
      );
    } finally {
      setPending(false);
    }
  };

  const sendIndependent = async () => {
    setPending(true);
    setError(null);
    try {
      await completeIndependentProfile({ name: trimmedName, consent });
      clearEnemIntent();
      await refresh();
      void navigate('/enem', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível concluir. Tente de novo.');
    } finally {
      setPending(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (nameError || trimmedName.split(' ').length < 2) return;
    if (independent) {
      if (wantsPhone(consent) && !phoneValid(consent.phone)) return;
      void sendIndependent();
      return;
    }
    if (chosen.length === 0 || (role === 'student' && !courseId) || !emailValid) return;
    if (wantsPhone(consent) && !phoneValid(consent.phone)) return;
    void send();
  };

  const checkNow = async () => {
    setChecking(true);
    await refresh();
    setChecking(false);
  };

  if (newInstitution) {
    return (
      <InstitutionSignupPage
        onBack={() => {
          clearPlanIntent();
          setNewInstitution(false);
          if (location.pathname.startsWith('/instituicoes')) void navigate('/', { replace: true });
        }}
      />
    );
  }

  if (sentTo) {
    return (
      <AuthLayout>
        <AuthHeader title="Confirme seu e-mail" />
        <FormError message={error} />
        <Alert severity="info" icon={<MarkEmailReadRounded />} sx={{ mb: 3 }}>
          <AlertTitle>Enviamos um link para {sentTo}</AlertTitle>
          Abra sua caixa de entrada (confira também o spam) e clique em “Confirmar meu e-mail”. Esta
          tela continua sozinha assim que você confirmar.
        </Alert>
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            onClick={() => void checkNow()}
            disabled={checking}
          >
            {checking ? <CircularProgress size={22} color="inherit" /> : 'Já confirmei'}
          </Button>
          <Button
            onClick={() => void send()}
            disabled={pending || cooldown > 0 || chosen.length === 0}
          >
            {cooldown > 0 ? `Reenviar e-mail (${cooldown}s)` : 'Reenviar e-mail'}
          </Button>
          <Button color="inherit" onClick={() => setSentTo(null)}>
            Corrigir meus dados
          </Button>
        </Stack>
        <SignOut onClick={() => void signOut()} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeader
        title="Complete seu cadastro"
        subtitle="Só falta isso para você acessar o Lousa. Confirme seus dados abaixo."
      />
      <FormError message={error} />
      {options.error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={options.reload}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar as instituições.
        </Alert>
      ) : options.loading ? (
        <Stack spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : (
        <Box component="form" noValidate onSubmit={submit}>
          <Stack spacing={2.5}>
            <FormField
              label="Nome completo"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={Boolean(nameError)}
              helperText={nameError ?? 'Como aparece nos seus documentos.'}
            />
            <FormField
              label="E-mail da conta Google"
              value={user.email}
              disabled
              helperText="É com esta conta que você entra no Lousa."
            />
            <Stack spacing={1}>
              <FormLabel id="profile-role">Você é</FormLabel>
              <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={independent ? 'independent' : role}
                onChange={(_, next: SignupRole | 'independent' | null) => {
                  if (next === null) return;
                  setIndependent(next === 'independent');
                  if (next !== 'independent') changeRole(next);
                }}
                aria-labelledby="profile-role"
              >
                <ToggleButton value="student">Estudante</ToggleButton>
                <ToggleButton value="teacher">Professor(a)</ToggleButton>
                <ToggleButton value="independent">Vou fazer o ENEM</ToggleButton>
              </ToggleButtonGroup>
              {independent && (
                <Typography variant="body2" color="text.secondary">
                  Para quem estuda por conta própria: sem instituição e sem e-mail institucional.
                  Você entra direto no Lousa ENEM, com simulados grátis toda semana.
                </Typography>
              )}
            </Stack>
            {independent ? null : role === 'student' ? (
              <>
                <FormField
                  select
                  label="Instituição"
                  value={institutionIds[0] ?? ''}
                  onChange={(e) => setInstitutionIds(e.target.value ? [e.target.value] : [])}
                  error={Boolean(institutionError)}
                  helperText={institutionError ?? 'Onde você estuda.'}
                >
                  {institutions.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {i.name}
                    </MenuItem>
                  ))}
                </FormField>
                <FormField
                  select
                  label="Curso"
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                  error={Boolean(courseError)}
                  helperText={courseError}
                >
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </FormField>
              </>
            ) : (
              <Stack spacing={0.5}>
                <FormLabel id="profile-institutions" error={Boolean(institutionError)}>
                  Onde você dá aula
                </FormLabel>
                <FormGroup aria-labelledby="profile-institutions">
                  {institutions.map((i) => (
                    <FormControlLabel
                      key={i.id}
                      control={
                        <Checkbox
                          checked={institutionIds.includes(i.id)}
                          onChange={() => toggleInstitution(i.id)}
                        />
                      }
                      label={i.name}
                    />
                  ))}
                </FormGroup>
                <FormHelperText error={Boolean(institutionError)}>
                  {institutionError ?? 'Marque todas as instituições em que você dá aula.'}
                </FormHelperText>
              </Stack>
            )}
            {!independent && (
              <FormField
                label="E-mail institucional"
                type="email"
                autoComplete="email"
                placeholder={
                  hint.split(' ou ')[0]?.replace(/ \(.*\)$/, '') || 'seu e-mail institucional'
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={Boolean(emailError)}
                helperText={
                  emailError ??
                  (alreadyConfirmed
                    ? 'Já confirmado pela sua conta Google.'
                    : chosen.length === 0
                      ? `Escolha ${role === 'student' ? 'sua instituição' : 'onde você dá aula'} para ver o formato.`
                      : `Formato: ${hint}. Vamos enviar um link para confirmar que ele é seu.`)
                }
              />
            )}
            <Stack spacing={1}>
              <FormLabel component="p">Quer receber novidades? (opcional)</FormLabel>
              <ConsentOptions
                value={consent}
                onChange={setConsent}
                email={user.email}
                phoneError={phoneError}
              />
            </Stack>
            <Button type="submit" variant="contained" size="large" fullWidth disabled={pending}>
              {pending ? (
                <CircularProgress size={22} color="inherit" />
              ) : independent ? (
                'Começar no Lousa ENEM'
              ) : alreadyConfirmed ? (
                'Concluir cadastro'
              ) : (
                'Enviar link de confirmação'
              )}
            </Button>
            {role === 'teacher' && !independent && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Depois de confirmar o e-mail, a coordenação aprova seu acesso como professor(a).
              </Typography>
            )}
          </Stack>
        </Box>
      )}
      {!independent && (
        <Button
          fullWidth
          variant="tonal"
          startIcon={<AddBusinessRounded />}
          onClick={() => setNewInstitution(true)}
          sx={{ mt: 3, justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
        >
          <Box component="span">
            Sua instituição não está na lista?
            <Typography component="span" variant="body2" sx={{ display: 'block', opacity: 0.8 }}>
              Cadastre-a e comece com dias grátis
            </Typography>
          </Box>
        </Button>
      )}
      <SignOut onClick={() => void signOut()} />
    </AuthLayout>
  );
}

function SignOut({ onClick }: { onClick: () => void }) {
  return (
    <Typography variant="body2" sx={{ textAlign: 'center', mt: 3 }}>
      <Button size="small" color="inherit" onClick={onClick}>
        Sair
      </Button>
    </Typography>
  );
}
